import { memo, useCallback, useMemo } from 'react';

import { OnOffShareToggleRow } from '~/components/ShareModal/components/OnOffShareToggleRow';
import { ToggleInfoTooltip } from '~/components/ShareModal/components/ToggleInfoTooltip';
import { UpsellFlashIconButton } from '~/components/ShareModal/components/UpsellFlashIconButton';
import { useShowcaseModal } from '~/components/ShowcaseModal/ShowcaseModalContext';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';

/** Create-mode smart leaf: file info & custom metadata visibility toggle. Writes draft state, no per-change analytics. */
export const CreateShowcaseSettingsMetadataRow = memo(() => {
  const { shortUrlProps, isSaving, updateMetadataVisibility } = useShowcaseModal();
  const { metadataEnabled } = useShareSettings();

  const showMetadata = useMemo(
    () => shortUrlProps.presentationSettings?.hideInfoTab !== true,
    [shortUrlProps.presentationSettings?.hideInfoTab],
  );

  const labelAccessory = useMemo(
    () =>
      metadataEnabled ? (
        <ToggleInfoTooltip text="Show file info and custom metadata to public viewers" />
      ) : (
        <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>
      ),
    [metadataEnabled],
  );

  const handleToggleMetadataVisibility = useCallback(
    (checked: boolean) => {
      updateMetadataVisibility(checked);
    },
    [updateMetadataVisibility],
  );

  return (
    <OnOffShareToggleRow
      label="File info & custom metadata"
      labelAccessory={labelAccessory}
      checked={showMetadata}
      isLoading={isSaving}
      disabled={!metadataEnabled}
      onToggle={handleToggleMetadataVisibility}
      data-testid="METADATA_VISIBILITY_TOGGLE"
    />
  );
});

CreateShowcaseSettingsMetadataRow.displayName = 'CreateShowcaseSettingsMetadataRow';
